/**
 * Custom Hook - useMetrics
 * Manages metrics and monitoring state
 */

import { useState, useEffect, useCallback } from 'react';
import { metricsService } from '../services/MetricsService';
import { RequestMetric, MetricsSummary, TokenUsageMetric } from '../repositories/MetricsRepository';

export function useMetrics(timeRange: string = '24h') {
  const [requestMetrics, setRequestMetrics] = useState<RequestMetric[]>([]);
  const [tokenMetrics, setTokenMetrics] = useState<TokenUsageMetric[]>([]);
  const [summary, setSummary] = useState<MetricsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load all metrics
   */
  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [requests, tokens, summaryData] = await Promise.all([
        metricsService.getRequestsOverTime(timeRange),
        metricsService.getTokenUsage(timeRange),
        metricsService.getSummaryMetrics(timeRange)
      ]);

      setRequestMetrics(requests);
      setTokenMetrics(tokens);
      setSummary(summaryData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      // Clear data on error
      setRequestMetrics([]);
      setTokenMetrics([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  /**
   * Refresh metrics
   */
  const refreshMetrics = useCallback(() => {
    loadMetrics();
  }, [loadMetrics]);

  // Load metrics on mount and when timeRange changes
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  return {
    requestMetrics,
    tokenMetrics,
    summary,
    loading,
    error,
    refreshMetrics
  };
}
